import { useCallback, useEffect, useState } from 'react';
import { s, authHeaders } from '../listPageStyles';
import { DOC_TYPE_OPTIONS, fmtDate, fmtMoney } from './eDocumentModel';
import './eDocument.css';

const API_BASE = '/api/edocuments';

function isoDaysAgo(days) {
  const d = new Date();
  d.setDate(d.getDate() - days);
  return d.toISOString().slice(0, 10);
}

/**
 * FAZ 6 — MUTABAKAT (spec §7). Ayrı bir çalışma modu.
 *
 * Bir tarih aralığında BİZİM GÖNDERDİĞİMİZ belgelerle GİB'İN KAYITLI OLDUĞU belgeler karşılaştırılır:
 *
 *   1. BİZDE VAR, GİB'DE YOK  → EN KRİTİK. Sistemimiz "gönderildi" diyor, GİB'de karşılığı yok.
 *                                Yasal olarak belge MEVCUT DEĞİL.
 *   2. GİB'DE VAR, BİZDE YOK  → başka kanaldan kesilmiş belge, ya da kaydımız kaybolmuş.
 *   3. İKİSİNDE DE VAR, FARKLI → tutar/tarih tutmuyor: veri bozulması.
 *
 * ⚠ ENTEGRATÖR YOKKEN KARŞILAŞTIRMA YAPILMAZ. Boş bir GİB listesi, TÜM belgelerimizi "GİB'de yok"
 * (en kritik küme) diye işaretlerdi — felaket bir yanlış-pozitif. Backend `gibQueried=false` döner
 * ve ekran bunu DÜRÜSTÇE söyler: sessizce "her şey yolunda" DEMEZ.
 */
export function EDocumentReconciliationModal({ open, onClose, showToast }) {
  const [from, setFrom] = useState(() => isoDaysAgo(30));
  const [to, setTo] = useState(() => new Date().toISOString().slice(0, 10));
  const [documentType, setDocumentType] = useState('');
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [tab, setTab] = useState('missingInGib');

  const run = useCallback(async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      if (from) params.set('from', new Date(from).toISOString());
      if (to) params.set('to', new Date(`${to}T23:59:59`).toISOString());
      if (documentType !== '') params.set('documentType', documentType);

      const res = await fetch(`${API_BASE}/reconciliation?${params.toString()}`, { headers: authHeaders() });
      const body = await res.json().catch(() => null);
      if (!res.ok) throw new Error(body?.message || body?.Message || 'Mutabakat çalıştırılamadı');
      setData(body?.data ?? body?.Data ?? null);
    } catch (err) {
      showToast?.('error', err.message || 'Mutabakat çalıştırılamadı');
      setData(null);
    } finally {
      setLoading(false);
    }
  }, [from, to, documentType, showToast]);

  useEffect(() => { if (open) run(); }, [open, run]);

  if (!open) return null;

  const tabs = [
    { key: 'missingInGib', label: 'Bizde var, GİB\'de yok', crit: true, rows: data?.missingInGib ?? [] },
    { key: 'missingInOurs', label: 'GİB\'de var, bizde yok', rows: data?.missingInOurs ?? [] },
    { key: 'mismatched', label: 'Tutar / tarih farklı', rows: data?.mismatched ?? [] },
  ];
  const active = tabs.find((t) => t.key === tab) || tabs[0];

  return (
    <div style={s.overlay} onClick={onClose}>
      <div style={{ ...s.modal, maxWidth: 880 }} onClick={(e) => e.stopPropagation()}>
        <div style={s.modalHeader}>
          <h3 style={{ margin: 0 }}>Mutabakat</h3>
          <button type="button" style={s.btnSecondary} onClick={onClose}>Kapat</button>
        </div>

        <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', alignItems: 'center', margin: '12px 0' }}>
          <input type="date" style={s.filterInput} value={from} onChange={(e) => setFrom(e.target.value)} />
          <span style={{ color: 'var(--text-secondary)' }}>–</span>
          <input type="date" style={s.filterInput} value={to} onChange={(e) => setTo(e.target.value)} />
          <select style={s.filterInput} value={documentType} onChange={(e) => setDocumentType(e.target.value)}>
            <option value="">Belge tipi: Tümü</option>
            {DOC_TYPE_OPTIONS.map((o) => <option key={o.value} value={o.value}>{o.label}</option>)}
          </select>
          <button type="button" style={s.btnSecondary} onClick={run} disabled={loading}>
            {loading ? 'Çalışıyor…' : 'Karşılaştır'}
          </button>
        </div>

        {/* ⚠ Entegratör yoksa KARŞILAŞTIRMA YAPILMAZ ve bunu açıkça söyleriz. Sessizce boş üç sekme
            göstermek, "mutabakat temiz" gibi okunurdu — oysa hiç sorgulanmadı. */}
        {data && data.gibQueried === false && (
          <div className="edoc-warnbox" style={{ marginBottom: 12 }}>
            {data.message}
          </div>
        )}

        <div className="edoc-modal-tabs">
          {tabs.map((t) => (
            <button
              key={t.key}
              type="button"
              className={`edoc-tab ${t.crit ? 'edoc-tab--crit' : ''} ${tab === t.key ? 'is-active' : ''}`}
              onClick={() => setTab(t.key)}
            >
              {t.label} ({t.rows.length})
            </button>
          ))}
        </div>

        <div style={{ maxHeight: 380, overflow: 'auto' }}>
          {active.rows.length === 0 ? (
            <div className="edoc-muted" style={{ padding: 20, textAlign: 'center' }}>
              {data?.gibQueried === false
                ? 'GİB sorgulanamadığı için karşılaştırma yapılmadı.'
                : 'Bu kümede kayıt yok.'}
            </div>
          ) : (
            <table className="edoc-lines">
              <thead>
                <tr>
                  <th>Belge No</th>
                  <th>ETTN</th>
                  <th>Tarih</th>
                  {tab === 'mismatched' ? (
                    <>
                      <th className="num">Bizdeki Tutar</th>
                      <th className="num">GİB Tutarı</th>
                    </>
                  ) : (
                    <th className="num">Net Tutar</th>
                  )}
                </tr>
              </thead>
              <tbody>
                {active.rows.map((r, i) => (
                  <tr key={r.id || r.ettn || i}>
                    <td className="edoc-mono">{r.documentNo || '—'}</td>
                    <td className="edoc-mono">{r.ettn ? String(r.ettn).slice(0, 8) + '…' : '—'}</td>
                    <td>{fmtDate(r.issuedAt ?? r.ourDate)}</td>
                    {tab === 'mismatched' ? (
                      <>
                        <td className="num">{fmtMoney(r.ourAmount)}</td>
                        <td className="num edoc-danger">{fmtMoney(r.gibAmount)}</td>
                      </>
                    ) : (
                      <td className="num">{fmtMoney(r.netAmount)}</td>
                    )}
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>

        {/* e-ARŞİV GÜNLÜK RAPOR MUTABAKATI (iş kuralı 5) — entegratörden BAĞIMSIZ çalışır, çünkü
            rapor bayrağı BİZİM kaydımızdır. Bu yüzden entegratör yokken bile doğru sonuç verir. */}
        {data?.archiveNotReported?.length > 0 && (
          <div className="edoc-warnbox" style={{ marginTop: 12 }}>
            <strong>e-Arşiv günlük rapor:</strong> {data.archiveNotReported.length} belge gönderildi
            ama <strong>günlük rapora girmedi</strong>. e-Arşiv zarfla değil raporla gider —
            "gönderildi" demek yetmez.
          </div>
        )}
      </div>
    </div>
  );
}

export default EDocumentReconciliationModal;
